import { Heading, StickyFooter } from '@/components';
import { withOneBoxCollapse } from '@/utils/orderBoxes';
import {
  CustomerDetailsCard,
  PackageAndPaymentCard,
  ProductDetailsCard,
  OrderIdCard,
  DeliveryInstructionCard,
  PickupAddressCard,
  ServiceLevelCard,
} from '@/components/OrderForm';
import NoCarrierModal from '@/components/OrderForm/NoCarrierModal';
import { ROUTE } from '@/config';
import { useChangeProduct, useUpdateOrderPricePreview } from '@/hooks';
import useHasServiceChoice from '@/hooks/useHasServiceChoice';
import useServiceStepDecision from '@/hooks/useServiceStepDecision';
import useBlockedServiceNotice from '@/hooks/useBlockedServiceNotice';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { useOrderByUID, useUpdateOrder } from '@/hooksApi/useOrder';
import { useFetchPickupAddresses, useGetAvailableCarriers } from '@/hooksApi';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import useNewOrderStore from '@/stores/useNewOrderStore';
import useProductStore from '@/stores/productStore';
import {
  AvailableCarrierItem,
  AvailableCarriersAdaptedResponse,
  BasicProduct,
  Order,
  OrderBoxInput,
  OrderItem,
  OrderOptionEnum,
  ServiceTypeEnum,
} from '@/types';
import {
  adjustPriceByOrderType,
  adjustProductListByOrderType,
  checkProductValidation,
  getOrderOptions,
  getOrderPickupAddress,
  getOrderTypeTextKey,
  parsePhoneNumber,
  buildAvailableCarriersRequest,
  processCarrierPreviewResponse,
} from '@/utils';
import { Flex, Form, message, Spin } from 'antd';
import { Store } from 'antd/es/form/interface';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useEffect, useState, KeyboardEvent, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import { ContactData } from '@/components/OrderForm/CustomerDetailsCard';
import { SmartPasteCard } from '@/components/SmartPaste';
import { useSmartPasteFill } from '@/hooks/useSmartPasteFill';
import {
  carrierRefusalReason,
  carrierRefusalCode,
  serviceLaneRefusal,
} from '@/utils/carrierRefusalReason';
import {
  buildRestoreFormValues,
  buildRestoreProducts,
} from '@/pages/add-new-order/restoreOrderForm';

interface EditOrderProps {
  keyValue?: string;
}

const EditOrder = ({ keyValue }: EditOrderProps) => {
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string | undefined>(undefined);
  const [showNoCarrierModal, setShowNoCarrierModal] = useState(false);
  // R56-F3 — the backend's localized refusal for the CURRENT failure.
  // Cleared on every open so a stale reason can never explain a new one.
  const [noCarrierReason, setNoCarrierReason] = useState<string | undefined>();
  // BOXY-301 (QA F-DASH-2) — travels with the sentence so the Boxy Select
  // branch of NoCarrierModal can tell a refusal it may safely render from one
  // it may not.
  const [noCarrierReasonCode, setNoCarrierReasonCode] = useState<string | undefined>();
  const { orderType, setOrderType, resetOrderType } = useOrderTypeStore();

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { orderUid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const hasServiceChoice = useHasServiceChoice();
  // SVC-STEP increment 2 — same decision, same source as create-order and the
  // stepper rail. An edit that skipped on different logic would be the SVC-F2
  // class again, with a worse blast radius.
  // BOXY-113 §6.5 — an address edit into a remote area re-checks the service.
  const destinationRegionUid = Form.useWatch('region_uid', form) as string | undefined;
  // BOXY-530 — and the pickup: a Basra pickup can only ship Boxy Select.
  const savedPickUpAddressUid = Form.useWatch('saved_pick_up_address_uid', form) as
    | string
    | undefined;
  const pickUpAddressUid = Form.useWatch('pick_up_address_uid', form) as string | undefined;
  // BOXY-547 — and the boxes: a multi-box order can only ship where a carrier takes it.
  const watchedBoxes = Form.useWatch('boxes', form) as OrderBoxInput[] | undefined;
  const { shouldShowStep, resolution } = useServiceStepDecision(
    selectedProvinceCode,
    true,
    undefined,
    destinationRegionUid,
    { saved_pick_up_address_uid: savedPickUpAddressUid, pick_up_address_uid: pickUpAddressUid },
    watchedBoxes,
  );
  const checkBlockedService = useBlockedServiceNotice(resolution);

  // Gates the auto-derive-fragile effect. Kept false while the fetched order
  // hydrates so the effect cannot clobber the restored order-level `isFragile`
  // (an order with is_fragile: true but NO individually-fragile product) before
  // the products have settled. Re-enabled once hydration finishes. Mirrors the
  // create page's fragileSyncEnabledRef.
  const fragileSyncEnabledRef = useRef(false);

  const { products, resetProducts, setSubmitAttempted } = useProductStore();
  const {
    setOrderPayload: setStoreOrderPayload,
    setCarrierSelection,
    setSelectedCarrier,
    setSourceRegionUid,
    setContactDisplayInfo,
    setEditMode,
    reset: resetOrderStore,
  } = useNewOrderStore();

  // Back from the carrier/service/summary step: the merchant's unsaved edit
  // lives in the store, and the mount effect below resets it. Captured first,
  // so the form re-opens with THEIR values, not the saved order's.
  const draftRef = useRef<{ payload: Order; provinceCode?: string | null } | null>(
    (() => {
      const store = useNewOrderStore.getState();
      const fromWizard = (location.state as { fromWizard?: boolean } | null)?.fromWizard;
      return fromWizard && store.editOrderUid === orderUid && store.orderPayload
        ? { payload: store.orderPayload, provinceCode: store.contactDisplayInfo?.provinceCode }
        : null;
    })(),
  );

  const { data: pickupAddressesData } = useFetchPickupAddresses(1);

  const { mutate: fetchCarrierPreview } = useGetAvailableCarriers();

  const {
    handleFormUpdated,
    orderPrice,
    setOrderPrice,
    orderPriceLoading,
    previewBlockedReason,
    previewBlockedCode,
    customShipmentFee,
    setCustomShipmentFee,
    setPackageSize,
    transformValuesToOrder,
    checkOrderFormValidation,
    handleProductChanged,
  } = useUpdateOrderPricePreview(products, form, false);
  // BOXY-597 — the order's service cannot reach the edited area (Boxy Save →
  // الحسينية). Under Select that is not a dead end: UPDATE stays live and takes
  // the merchant to the service step with the edit kept (see checkCarriersAndNavigate).
  const canPickAnotherService = hasServiceChoice;
  const serviceLaneBlocked =
    canPickAnotherService && previewBlockedCode === 'SERVICE_NO_ELIGIBLE_CARRIER';
  const submitBlockedReason = serviceLaneBlocked ? null : previewBlockedReason;

  const {
    handleAddProduct,
    handleAddProducts,
    handleDeleteProduct,
    setProducts,
    handleUpdateProduct,
    handleUpdateProductBatch,
    setIsProductListValid,
  } = useChangeProduct(handleProductChanged, orderType);

  const { mutate: mutateEditOrder, isLoading: editOrderLoading } = useUpdateOrder(orderUid!);
  const {
    data: orderData,
    isLoading: displayOrderLoading,
    isFetching: displayOrderFetching,
  } = useOrderByUID(orderUid!); // Use useOrderByUID to fetch the order data
  const { data: provinceList, isLoading: provinceListLoading } = useFetchListProvinces();
  const { data: areaList, isLoading: areaListLoading } = useFetchAreaByProvinceCode(
    selectedProvinceCode!,
  );

  // Memoize the adjusted products to prevent unnecessary recalculations
  const adjustedProducts = useMemo(() => {
    if (!orderData?.object?.products) return [];
    return adjustProductPrices(orderData.object.products);
  }, [orderData?.object?.products, orderData?.object?.type]);

  useEffect(() => {
    // Reset all stores on mount to clear stale data from previous order flows
    resetOrderStore();
    form.resetFields();
    resetProducts();
    setIsProductListValid(true);
    resetOrderType();

    return () => {
      form.resetFields();
      resetProducts();
      setIsProductListValid(true);
      resetOrderType();
    };
  }, []);

  useEffect(() => {
    if (orderData && provinceList && !selectedProvinceCode) {
      fetchSelectedProvince();
    }
    if (!areaListLoading && areaList && areaList?.object.length > 0) {
      const regionUid =
        draftRef.current?.payload.contact?.region_uid ?? orderData?.object.region?.uid;
      const area = areaList.object.find((item) => item.uid === regionUid);

      form.setFieldsValue({ region_uid: area?.uid });
    }
  }, [areaListLoading, areaList, orderData, keyValue, provinceList]);

  useEffect(() => {
    form.resetFields();
    // Suppress the auto-derive-fragile effect until this order finishes
    // hydrating, so it can't wipe the restored isFragile mid-hydration.
    fragileSyncEnabledRef.current = false;

    if (orderData) {
      setOrderPrice({
        ...orderData.object.preview,
      });

      setCustomShipmentFee(orderData.object.fee_customer_payable, true);

      if (orderData?.object?.products) {
        setProducts(
          adjustProductListByOrderType(
            orderData.object.products,
            orderData.object.type as OrderTypeEnum,
          ),
        );
      }

      fetchSelectedProvince();

      const primaryPhoneInfo = parsePhoneNumber(orderData.object.phone);
      const additionalPhones: string[] | undefined = orderData.object.additional_phones
        ? JSON.parse(orderData.object.additional_phones)
        : undefined;

      const secondaryPhoneInfo = parsePhoneNumber(additionalPhones?.[0]);

      // Prepopulate the form with fetched order data
      form.setFieldsValue({
        orderType: orderData.object.type,
        name: orderData.object?.full_name || '',
        address: orderData.object?.address_text || '',
        deliveryInstruction: orderData?.object?.description || '',
        packageSize: orderData?.object?.size || '',
        // BOXY-301 — a multi-box order opens with its boxes, not as one parcel.
        boxes:
          Array.isArray(orderData?.object?.boxes) && orderData.object.boxes.length >= 2
            ? orderData.object.boxes.map((box: { size: string }) => ({ size: box.size }))
            : undefined,
        // The service the order is ACTUALLY on, so the picker opens showing
        // the truth rather than an empty box. Unlike create — where an empty
        // field means "use my standing rules" — an existing order already
        // resolved to one service, and hiding that would make every edit look
        // like a fresh choice.
        serviceCode: orderData?.object?.service_code || undefined,
        isFragile: orderData?.object?.is_fragile,
        temperatureControl: !!orderData?.object?.temperature_control,
        CodOrPrepaid: orderData?.object?.payment_type,
        CustomerOrMerchant: orderData?.object?.shipment_fee_type,
        orderId: orderData?.object?.custom_id,
        pickupOrDropoff: orderData?.object?.pick_up_type,
        saved_pick_up_address_uid: getOrderPickupAddress(orderData.object as unknown as OrderItem)
          ?.uid,
        freezed_pick_up_address_title:
          orderData.object.status.slug !== 'new'
            ? orderData.object.pick_up?.pick_up_address?.title
            : undefined,
        phoneNumberPrimary: primaryPhoneInfo.phone || '',
        countryCodePrimary: primaryPhoneInfo.countryCode,
        countryCodeSecondary: secondaryPhoneInfo?.countryCode,
        secondaryPhoneNumber: secondaryPhoneInfo?.phone,
        products: adjustedProducts,
        ...getOrderOptions(orderData.object.options),
      });

      // Set OrderType state after form initialization
      if (orderData.object.type) {
        setOrderType(orderData.object.type as OrderTypeEnum);
      }

      const draft = draftRef.current?.payload;
      if (draft) {
        // `boxes: undefined` first: a draft collapsed to one box has no list.
        form.setFieldsValue({
          boxes: undefined,
          ...buildRestoreFormValues(draft, draftRef.current?.provinceCode),
        });
        setProducts(buildRestoreProducts(draft.products));
        setCustomShipmentFee(draft.fee_customer_payable ?? undefined, true);
        if (draft.type) setOrderType(draft.type as OrderTypeEnum);
      }

      // Re-enable the fragile auto-derive only after the restored products and
      // form values have hydrated, so it can't wipe the restored isFragile.
      setTimeout(() => {
        fragileSyncEnabledRef.current = true;
      }, 0);
    }
  }, [orderData, form, keyValue, adjustedProducts, setOrderType]);

  const resolveSourceRegionUid = useCallback(
    (formValues: Store): string | undefined => {
      const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
        (addr) =>
          addr.uid === formValues.saved_pick_up_address_uid ||
          addr.uid === formValues.pick_up_address_uid,
      );
      // Fallback chain for scheduled/in-progress orders where pickup address
      // may not be in the paginated list or region may not be eager-loaded
      return (
        selectedPickupAddress?.region?.uid ??
        orderData?.object?.pick_up?.pick_up_address?.region?.uid ??
        orderData?.object?.saved_pick_up_address?.region?.uid
      );
    },
    [pickupAddressesData, orderData],
  );

  // No merchant picks a carrier (owner 2026-10-28), so there is no rule
  // evaluation and no carrier step on an edit — the flow Select mode always had.
  const checkCarriersAndNavigate = (orderPayload: Order, formValues: Store) => {
    const source_region_uid = resolveSourceRegionUid(formValues);

    if (!source_region_uid || !orderPayload.contact?.region_uid) {
      console.warn('Cannot check carrier availability: missing pickup or destination region.');
      // Client-side precondition — no backend answer exists.
      setNoCarrierReason(undefined);
      setNoCarrierReasonCode(undefined);
      setShowNoCarrierModal(true);
      return;
    }

    // BOXY-588 — the edit moved the order where its service cannot go (Boxy
    // Save → a remote area). The PATCH would refuse it, and asking for carriers
    // on that service only ends in the generic "no delivery option" modal. Send
    // the merchant to the service step instead, priced on the fallback service,
    // with the edit kept.
    // BOXY-597 — the price preview's refusal is the same signal when the
    // resolve answer is unknown; the backend then picks the fallback itself.
    const blocked = canPickAnotherService
      ? (checkBlockedService(orderPayload.service_code ?? orderData?.object?.service_code) ??
        (serviceLaneBlocked && previewBlockedReason
          ? { notice: previewBlockedReason, fallback: undefined }
          : undefined))
      : undefined;
    if (blocked) {
      proceedWithCarrierFetch(
        { ...orderPayload, service_code: blocked.fallback },
        source_region_uid,
        blocked.notice,
      );
      return;
    }

    proceedWithCarrierFetch(orderPayload, source_region_uid);
  };

  // Check the edited order can ship, then go to the service step or summary.
  const proceedWithCarrierFetch = (
    orderPayload: Order,
    source_region_uid: string,
    serviceRefusal?: string,
  ) => {
    const carrierRequest = buildAvailableCarriersRequest(orderPayload, source_region_uid);

    fetchCarrierPreview(carrierRequest, {
      onSuccess: (response: AvailableCarriersAdaptedResponse) => {
        const allCarriers = processCarrierPreviewResponse(response);

        if (allCarriers.length === 0) {
          // A 200 with nothing in it — no refusal sentence to quote.
          setNoCarrierReason(undefined);
          setNoCarrierReasonCode(undefined);
          setShowNoCarrierModal(true);
          return;
        }

        // Build AvailableCarrierItem[] for the store
        const available: AvailableCarrierItem[] = allCarriers.map((c) => ({
          carrier_slug: c.carrier.slug || '',
          carrier_name: c.carrier.title,
          carrier_logo_url: c.carrier.avatar?.abs_url || '',
          shipping_fee: parseFloat(c.shipping_fee) || 0,
          delivery_day:
            c.serviceType === ServiceTypeEnum.SAME_DAY ? ('same' as const) : ('next' as const),
          same_day_cutoff: c.serviceType === ServiceTypeEnum.SAME_DAY ? c.if_ship_before : null,
          drop_off_time: c.serviceType === ServiceTypeEnum.NEXT_DAY ? c.if_ship_before : null,
        }));

        setStoreOrderPayload(orderPayload);
        setSourceRegionUid(source_region_uid);
        setEditMode(orderUid!);

        // Resolve province/area display names for the summary page
        const provinceName =
          provinceList?.object?.find((p) => p.province_code === selectedProvinceCode)?.province ??
          '';
        const areaName =
          areaList?.object?.find((a) => a.uid === orderPayload.contact?.region_uid)?.area_name ??
          '';
        // provinceCode rides with the name it was derived from, so the
        // summary's trace can never resolve destination-blind (QA INC2-F2).
        setContactDisplayInfo({
          provinceName,
          areaName,
          provinceCode: selectedProvinceCode ?? null,
        });
        setCarrierSelection({
          process_id: response.process_id,
          recommended_carrier_slug: response.recommended_carrier_slug,
          available_carriers: available,
        });

        // No carrier step (owner 2026-10-28). The pick is never sent; the
        // first entry is only the price the summary shows while it loads.
        setSelectedCarrier(available[0] ?? null);
        // QA SVC-F1 — the service step is on the EDIT path too, and this
        // navigate was the hole. Under Select the service is the merchant's
        // ONLY lever on how the shipment travels (see the note on
        // ServiceLevelCard below); that card moved into the step, so an edit
        // that goes straight to the summary leaves them unable to see it,
        // change it, or clear it. Same >=2 guard as create, from the same
        // shared source, so all three surfaces agree.
        if (serviceRefusal) {
          navigate(ROUTE.SELECT_SERVICE, { state: { serviceRefusal } });
        } else {
          navigate(hasServiceChoice && shouldShowStep ? ROUTE.SELECT_SERVICE : ROUTE.ORDER_SUMMARY);
        }
      },
      onError: (error: any) => {
        // BOXY-597 — the service carried on the edit (a pick from an earlier
        // visit to the step) cannot reach the new area: ask again without it
        // and land on the service step with the backend's sentence. Once only —
        // the retry carries no service_code.
        const laneRefusal =
          canPickAnotherService && orderPayload.service_code
            ? serviceLaneRefusal(error)
            : undefined;
        if (laneRefusal) {
          proceedWithCarrierFetch(
            { ...orderPayload, service_code: undefined },
            source_region_uid,
            laneRefusal,
          );
          return;
        }
        console.error('Failed to fetch carrier availability on update:', error);
        // R56-F3 — show the backend's reason, not an assumed one.
        setNoCarrierReason(carrierRefusalReason(error));
        setNoCarrierReasonCode(carrierRefusalCode(error));
        setShowNoCarrierModal(true);
      },
    });
  };

  const onFinish = (values: Store) => {
    setSubmitAttempted(true);

    // Check for partially filled products (has some data but missing required fields)
    const partiallyFilled = products.filter((p) => {
      const hasTitle = !!p.title;
      const hasPrice = !!p.price || p.price === 0;
      return (hasTitle || hasPrice) && !(hasTitle && hasPrice);
    });

    if (partiallyFilled.length > 0) {
      message.error(t('please_fill_all_product_fields'));
      return;
    }

    // Filter out completely empty products
    const finalProducts = products.filter((p) => checkProductValidation(p));

    const orderValue = { products: finalProducts, ...values };
    const isValid = checkOrderFormValidation(orderValue);
    setIsProductListValid(finalProducts.length > 0);

    if (isValid) {
      const orderPayload: Order = withOneBoxCollapse(
        transformValuesToOrder(orderValue, undefined, true),
        Array.isArray(orderData?.object?.boxes) ? orderData.object.boxes.length : 0,
      );
      checkCarriersAndNavigate(orderPayload, values);
    }
  };
  const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
    const formValues = form.getFieldsValue();

    checkOrderFormValidation({ ...formValues, products });

    console.warn('Failed: ', { ...errorInfo, products });
  };

  const fetchSelectedProvince = () => {
    const wanted = draftRef.current?.provinceCode || orderData?.object.region?.province_code;
    const provinceCode = provinceList?.object.find(
      (item) => item.province_code === wanted,
    )?.province_code;

    if (provinceCode && provinceCode !== selectedProvinceCode)
      setSelectedProvinceCode(provinceCode);

    form.setFieldValue('province', provinceCode);
    return provinceCode;
  };
  // Smart Paste — re-fill this order from a pasted message. Only the fields the
  // message actually names are touched; everything already on the order stays.
  const handleSmartPasteApply = useSmartPasteFill({
    form,
    products,
    provinces: provinceList?.object,
    setSelectedProvinceCode,
    setProducts,
    setOrderType,
    onFilled: (allValues, filledProducts) =>
      handleFormUpdated(
        { region_uid: allValues.region_uid },
        { ...allValues, products: filledProducts },
      ),
  });

  const handleSetSelectedProvinceCode = (provinceCode?: string) => {
    form.setFieldsValue({ province: provinceCode });
    form.setFieldsValue({ region_uid: undefined });
    form.setFieldValue('region_uid', undefined);
    setSelectedProvinceCode(provinceCode);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleCreateProduct = () => {
    handleAddProduct({ title: '', quantity: '1', price: '' });
  };

  // Auto-derive order-level fragile from products — set true when any is fragile, clear when none are.
  // Suppressed during the initial order hydration so it can't clobber the restored isFragile
  // (an order saved with is_fragile: true but no individually-fragile product).
  useEffect(() => {
    if (!fragileSyncEnabledRef.current) return;
    const anyFragile = products.some((p) => p.is_fragile === true);
    const currentFragile = form.getFieldValue('isFragile');
    if (anyFragile && !currentFragile) {
      form.setFieldsValue({ isFragile: true });
    } else if (!anyFragile && currentFragile) {
      form.setFieldsValue({ isFragile: false });
    }
  }, [products]);

  function adjustProductPrices(products: BasicProduct[]): BasicProduct[] {
    if (!products?.length) return products;

    return products.map((product) => ({
      ...product,
      // Preserve price type to avoid scientific notation for large values
      price:
        typeof product.price === 'string'
          ? String(Math.abs(parseFloat(product.price)))
          : Math.abs(Number(product.price)),
    }));
  }

  const handleOrderTypeChange = (newOrderType: OrderTypeEnum) => {
    const allValues = form.getFieldsValue();
    const updatedProducts = adjustProductListByOrderType(products, newOrderType);
    setProducts(updatedProducts);
    handleFormUpdated(
      { orderType: newOrderType },
      { ...allValues, orderType: newOrderType, products: updatedProducts },
    );
  };

  const onFormValuesChange = (changedValues: any, allValues: Store) => {
    const changedItem = Object.keys(changedValues)[0];
    if (changedItem === 'orderType') {
      const updatedProducts = adjustProductListByOrderType(products, allValues.orderType);
      setProducts(updatedProducts);
      handleFormUpdated(changedValues, { ...allValues, products: updatedProducts });
    } else {
      handleFormUpdated(changedValues, allValues);
    }
  };

  const handleUpdatePreviewByContactSelection = (contact: ContactData) => {
    const allValues = form.getFieldsValue();

    handleFormUpdated({ address: contact.address || '' }, { ...allValues, ...contact });
  };

  return (
    <Flex vertical>
      {/* Never CARRIER copy — the failure still has to be said, or UPDATE
          looks dead. */}
      <NoCarrierModal
        open={showNoCarrierModal}
        onClose={() => setShowNoCarrierModal(false)}
        reason={noCarrierReason}
        reasonCode={noCarrierReasonCode}
      />
      {displayOrderLoading || displayOrderFetching ? (
        <Spin size='large' className='flex justify-center items-center' />
      ) : (
        <Form
          form={form}
          name='add-new-order'
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onFormValuesChange}
          onKeyDown={handleKeyPress}
        >
          {/* 3-step wizard matching create order flow */}
          <OrderStepper currentStep={1} provinceCode={selectedProvinceCode} />

          <SmartPasteCard onApply={handleSmartPasteApply} />

          <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 mb-3'>
            {/* Left column */}
            <div className='flex flex-col gap-6 [&>*]:!mb-0'>
              <CustomerDetailsCard
                handleUpdatePreview={handleUpdatePreviewByContactSelection}
                form={form}
                selectedProvinceCode={selectedProvinceCode}
                setSelectedProvinceCode={handleSetSelectedProvinceCode}
              />
              <DeliveryInstructionCard />
              <OrderIdCard />
              <ProductDetailsCard
                products={products}
                handleCreateProduct={handleCreateProduct}
                handleDeleteProduct={handleDeleteProduct}
                handleUpdateProduct={handleUpdateProduct}
                handleUpdateProductBatch={handleUpdateProductBatch}
                handleAddProducts={handleAddProducts}
              />
              <PickupAddressCard
                form={form}
                orderStatus={orderData?.object.status.slug}
                previewOrderPrice={(formValue) =>
                  handleFormUpdated(
                    { saved_pick_up_address_uid: formValue.saved_pick_up_address_uid },
                    formValue,
                  )
                }
              />
            </div>
            {/* Right column */}
            <div className='flex flex-col gap-6 [&>*]:!mb-0'>
              <div className='sticky top-4 flex flex-col gap-4 [&>*]:!mb-0'>
                {/* No merchant picks a carrier, so the service is their only
                    lever on how the shipment travels. SVC-STEP: that lever
                    lives in the Select Service step, which this page's submit
                    routes through; this card only keeps the field registered. */}
                <ServiceLevelCard />
                <PackageAndPaymentCard
                  orderOptions={orderData?.object?.options ?? []}
                  orderStatus={orderData?.object.status.slug}
                  form={form}
                  onPackageSizeChange={setPackageSize}
                  orderPrice={{
                    ...orderPrice,
                    orders_shipment_fee: 0,
                    orders_custom_shipment_fee: 0,
                  }}
                  orderPriceLoading={orderPriceLoading}
                  blockedReason={previewBlockedReason}
                  paymentType={orderData?.object?.payment_type}
                  shipmentFeeType={orderData?.object?.shipment_fee_type}
                  packageSize={orderData?.object?.size}
                  onCustomShipmentFeeChange={setCustomShipmentFee}
                  customShipmentFee={customShipmentFee}
                  discountAmount={orderData?.object.discount_amount ?? 0}
                  prepaymentAmount={orderData?.object.prepayment_amount ?? 0}
                  onOrderTypeChange={handleOrderTypeChange}
                  hasFragileProduct={products.some((p) => p.is_fragile === true)}
                />
              </div>
            </div>
          </div>

          <StickyFooter
            cardClassName='rounded-2xl'
            actionButton={
              <div className='flex min-w-0 items-center gap-3'>
                {/* QA BOXY-588: the reason UPDATE is grey, in the bar itself — the
                    alert that also says it sits below the fold. */}
                {submitBlockedReason ? (
                  <p
                    id='submit-blocked-reason'
                    role='status'
                    className='m-0 line-clamp-2 max-w-md text-xs text-sd-destructive'
                  >
                    <span className='font-semibold'>{String(t('update_blocked_label'))}</span>{' '}
                    {submitBlockedReason}
                  </p>
                ) : null}
                <button
                  type='submit'
                  className='flex items-center gap-1.5 h-11 px-6 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-sm font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
                  // The backend has already refused to price this order; saving
                  // would fail with the same message. Gated until the merchant
                  // changes whatever the notice above the summary names.
                  disabled={editOrderLoading || !!submitBlockedReason}
                  title={submitBlockedReason ?? undefined}
                  aria-describedby={submitBlockedReason ? 'submit-blocked-reason' : undefined}
                  data-testid='submit-order'
                >
                  {editOrderLoading && (
                    <span className='h-4 w-4 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                  )}
                  {t('update')}
                </button>
              </div>
            }
            additionalContent={
              orderType !== OrderTypeEnum.NORMAL ? (
                <Heading
                  title={t(getOrderTypeTextKey(orderType))}
                  fontSize='text-sm'
                  fontWeight='font-bold'
                  extraClassName='uppercase leading-5 text-customTunaPrimary'
                />
              ) : null
            }
          />
        </Form>
      )}
    </Flex>
  );
};

export default EditOrder;
